import { formatNumber } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  effect,
  viewChild,
  viewChildren
} from '@angular/core';
import {
  BankState,
  LegDescription,
  QuoteDescriptionPayload,
  QuotePair,
  RFQ_PROVIDER_STATE_DECLINED,
  RFQ_PROVIDER_STATE_EXPIRED,
  RFQ_PROVIDER_STATE_UNQUOTED,
  RFQ_QUOTE_INDICATIVE,
  RFQ_STATUS_BLOCKED,
  RFQ_STATUS_ERROR,
  RFQ_STATUS_PENDING,
  RFQ_STATUS_RFE_PENDING,
  RFQ_STATUS_STREAMING,
  RFQ_STATUS_TRADED,
  RFQ_STATUS_WAITING,
  RFQ_TOP_OF_BOOK,
  RFQ_VIEW_CHANGE,
  RfeAction,
  RfqActionData,
  RfqBidOfferQuotes,
  RfqQuote,
  RfqSettingsState,
  RfqSettingsStateKey,
  RfqTab,
  UserPreferencesState,
  UserPreferencesStateKey
} from '@approot/models';
import { RfqMessagesService } from '@approot/rfq-messages/rfq-messages.service';
import {
  AdminService,
  AlertsService,
  ConfigService,
  CostAnalysis,
  IndicativeQuotes,
  MidPriceInfo,
  PhoenixService,
  Points,
  ProductsConfigService,
  RfqService,
  RfqTabService,
  RfqValidationStatus,
  UserStorageService
} from '@approot/services';
import { RfqOutcome } from '@approot/services/rfq/rfq-tab/rfq-tab';
import { mungeIndicativeQuotes } from '@approot/shared/custom-components/rfq-quotes/quotes';
import { ModalComponent } from '@approot/ui-kit';
import {
  clone,
  reformatDate,
  sleep,
  splitStringByMaxLength
} from '@approot/utilities';
import { QuoteTimeLimitFilterPipe } from '@approot/widgets/rfq';
import copy from 'copy-to-clipboard';
import { isEqual } from 'lodash';
import { Throttle } from 'lodash-decorators';
import { RfqBookTradesComponent } from '../rfq-book-trades/rfq-book-trades.component';
import { RfqSetupService } from '../rfq-setup/rfq-setup.service';
import { RfqTabComponent } from '../rfq-tab.component';

type DIResponse = {
  content: {
    message: string;
  };
};

@Component({
  selector: 'app-rfq-prices',
  templateUrl: './rfq-prices.component.html',
  styleUrls: ['./rfq-prices.component.scss']
})
export class RfqPricesComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public rfqPricingMessages: RfqValidationStatus;
  @Input() public parentTabComponent: RfqTabComponent;
  @Input({ required: true }) public bookTradesCmpt: RfqBookTradesComponent;
  @Input() public indicativePremiumStyle: string;
  @Input() public pipsHighlightedIndices: number[] = [];
  @Input() public forwardPointsMultiplier: number;
  @Input() public rfqTabData: RfqTab;
  @Input() public bookingState: string;
  @Input() public nonDxExists: boolean;
  @Output() public pricerToMainTabCommunicator =
    new EventEmitter<RfqActionData>();
  @Output() public removeIndicativePrices = new EventEmitter();
  @ViewChild(ModalComponent) public modal: ModalComponent;
  public cross: string;
  public warningScreenData = {
    warningScreenOpen: false,
    messageToDisplay: '',
    headerMessage: ''
  };
  public isNaN = isNaN;
  public hasPingedOnce = false;

  public selectedQuoteLegDescription: LegDescription = {
    description: [],
    valueChanged: false
  };

  public quotePair: QuotePair;
  public termPair: string;
  public costAnalysisGrid: string[][] = [];
  public rfqOutcomes: RfqOutcome[] = [
    'TRADED AWAY',
    'DID NOT TRADE',
    'IDEA',
    'OTHER'
  ];
  public rfqOutcomeModal = this.rfqTabService.rfqOutcomeModal;
  public indicativeQuotes: IndicativeQuotes;
  public pricingMessage: string;
  public copyQuoteState: 'COPIED' | 'COPYING' | 'ERROR' | 'COPY_TRADE' | null =
    null;
  public currentRfeID: number;
  public midPriceInfo: MidPriceInfo;
  public rfqRunning = false;
  public currentRfeExternalID: string;
  public enableCustomHedgeAmount: boolean;
  public canCopyQuote: boolean;
  public parent = '';
  private legacyWhiteLabelProvider: string = null;
  private lastPhxLogEvent: any = null;
  private subAccountNotSet = false;
  private quoteTimeLimitFilterPipe = new QuoteTimeLimitFilterPipe();
  private rfeTimeoutDialog =
    viewChild<ElementRef<HTMLDialogElement>>('rfeTimeoutDialog');
  private bidTableRowContainer = viewChildren<ElementRef>(
    'bidTableRowContainer'
  );
  private offerTableRowContainer = viewChildren<ElementRef>(
    'offerTableRowContainer'
  );
  private tableContainer = viewChild<ElementRef>('tableContainer');
  private exampleOfQuoteHeight: number;
  private maxQuotesOnSides: number;
  private currBanksState: BankState[] = [];
  private indicativePrices = this.rfqTabService.indicativeQuotes;
  private lastDIMsg: DIResponse;
  private lastShowBookTrades = false;
  private lastShowBookTradesMessage: any = {};
  private showBookTradesTimer: any;
  public showBookTrades: boolean = false;

  constructor(
    public rfqTabService: RfqTabService,
    private rfqService: RfqService,
    private adminService: AdminService,
    private phxService: PhoenixService,
    private userStorageService: UserStorageService,
    public rfqSetupService: RfqSetupService,
    private productsConfigService: ProductsConfigService,
    private alertsService: AlertsService,
    private renderer: Renderer2,
    private rfqMessagesService: RfqMessagesService,
    private configService: ConfigService
  ) {
    const medusaUser = this.adminService.medusaUser;
    if (medusaUser?.useMedusalegacyWhitelabel === true) {
      this.legacyWhiteLabelProvider =
        medusaUser.parameters?.RFQDetailsDTO?.legacyWhiteLabelProvider;
      if (this.legacyWhiteLabelProvider?.length === 0) {
        this.legacyWhiteLabelProvider = null;
      }
    }

    this.parent = this.configService.userSettings().parent.identifier;

    this.showBookTradesTimer = setInterval(() => {
      this.showBookTrades = this.updateShowBookTrades();
    }, 500);

    effect(() => {
      this.rfqTabService.statusTypeUpdated();
      this.rfqTabService.lastHandleVolBasedHedgeExecution();
      this.showBookTrades = this.updateShowBookTrades();
    });

    effect(_ => {
      const indicativeQuotes = this.rfqTabService.indicativeQuotes();
      if (this.legacyWhiteLabelProvider) {
        this.indicativeQuotes = indicativeQuotes;
      } else {
        this.indicativeQuotes = mungeIndicativeQuotes(indicativeQuotes);
      }
    });

    effect(_ => {
      this.onProviderAvailabilityChange(
        this.rfqTabService.selectedBanksState()
      );
    });

    effect(_ => {
      const maxCurrQuotes = Math.max(
        this.bidTableRowContainer().length,
        this.offerTableRowContainer().length
      );
      this.updateTableContainerHeight(maxCurrQuotes);
    });

    effect(_ => {
      const rfqRunning = this.rfqTabService.rfqRunning();
      if (rfqRunning && !this.rfqRunning) {
        this.lastShowBookTrades = false;
        this.lastShowBookTradesMessage = {};
      }
      this.rfqRunning = rfqRunning;
    });

    effect(_ => {
      this.midPriceInfo = this.rfqTabService.midPriceInfo();
    });

    effect(() => {
      const data = this.rfqTabService.lastVolBasedRfqOptionPriceV2Response();
      if (data?.costAnalysis) {
        this.costAnalysisGrid = this.buildCostAnalysisGrid(data.costAnalysis);
      }
    });

    effect(() => {
      const lqe = this.rfqTabService.lastQuoteEvents();
      const q = [...(lqe?.values() || [])];
      this.canCopyQuote = q.length > 0;
    });

    effect(_ => {
      const message = this.rfqMessagesService.message();

      const di = message.dealerIntervention as DIResponse;
      if (di && !isEqual(di, this.lastDIMsg)) {
        this.lastDIMsg = di;
        const msg = di.content?.message;
        if (msg) {
          this.alertsService.addAlert({ message: msg, type: 'info' });
        }
      }
    });
  }

  public ngOnInit(): void {
    this.enableCustomHedgeAmount =
      this.rfqTabData.executionContext.enableCustomHedgeAmount;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.rfqPricingMessages?.currentValue) {
      if (this.rfqPricingMessages?.messages) {
        this.pricingMessage = splitStringByMaxLength(
          this.rfqPricingMessages.messages,
          30
        ).join('\x0a');
      } else {
        this.pricingMessage = null;
      }
    }
  }

  public ngOnDestroy(): void {
    this.rfqTabService.wipeAllTimers();
    clearInterval(this.showBookTradesTimer);
  }

  public setupFormValue() {
    return this.rfqSetupService.setupForm.getRawValue();
  }

  @Throttle(250)
  public customHedgeAmount() {
    return (
      this.enableCustomHedgeAmount &&
      this.rfqSetupService.setupForm.getRawValue().hedgeCustomAmount
    );
  }

  public setInfoBasedOnQuotePair(): void {
    this.quotePair = this.rfqTabService.tabData.title.quotePair;
    this.cross = `${this.quotePair.base}${this.quotePair.term}`;
    this.termPair = `${this.quotePair.term}/${this.quotePair.base}`;
  }

  @Throttle(500)
  public get showCopyQuote(): boolean {
    if (
      !(
        this.rfqTabService.tabData.executionContext.enableCopyQuote &&
        this.rfqSetupService.setupForm.getRawValue().banks.length === 1
      )
    ) {
      return false;
    }

    if (
      !!this.indicativePrices()['bid']?.quote ||
      !!this.indicativePrices()['ask']?.quote
    ) {
      return false;
    }

    const status = this.rfqTabService.tabData.title.status.statusType;
    if (status === RFQ_STATUS_TRADED) {
      this.copyQuoteState = 'COPY_TRADE';
      return true;
    }

    let bids = this.quotes?.bids;
    if (bids?.length === 1) {
      bids = this.quoteTimeLimitFilterPipe.transform(
        bids,
        this.rfqService.currentTimestamp
      );
    }

    let offers = this.quotes?.offers;
    if (offers?.length === 1) {
      offers = this.quoteTimeLimitFilterPipe.transform(
        offers,
        this.rfqService.currentTimestamp
      );
    }

    return bids?.length === 1 || offers?.length === 1;
  }

  public getTradeRecap() {
    if (this.copyQuoteState !== 'COPY_TRADE') {
      return;
    }
    let payload = this.bookTradesCmpt?.getNonVolBasedPayload();
    if (!payload) {
      return;
    }
    this.copyQuoteState = 'COPYING';
    delete payload['task'];
    this.rfqService
      .tradeService({ task: 'getTradeRecap', ...payload })
      .subscribe({
        next: async (r: { description: string }) => {
          const text = r.description;
          copy(text, { format: 'text/html' });
          this.copyQuoteState = 'COPIED';
          setTimeout(() => (this.copyQuoteState = 'COPY_TRADE'), 3000);
        },
        error: () => {
          this.copyQuoteState = 'ERROR';
          setTimeout(() => (this.copyQuoteState = 'COPY_TRADE'), 3000);
        }
      });
  }

  public getQuoteDescription() {
    if (this.copyQuoteState != null) {
      return;
    }
    this.copyQuoteState = 'COPYING';

    const latestQuoteEvent = [
      ...(this.rfqTabService.lastQuoteEvents()?.values() || [])
    ][0];
    const payload = {
      latestQuoteEvent,
      rfqDetailsDTO: this.rfqTabService.rfqDetailsDTOMapped,
      hydra: {
        legs: this.rfqTabService.createProductsObject(true),
        setup: this.rfqSetupService.setup?.form.getRawValue(),
        hedges: this.rfqSetupService.setup?.getRawHedges(),
        hedgeQuotes: [...this.quotes.bids, ...this.quotes.offers]
          .filter(
            quote =>
              quote.timeLimit >= this.currentTimestamp || quote.frozenQuote
          )
          .map(quote =>
            this.rfqTabService.hedgeQuoteMessages().get(quote.quoteReference)
          )
          .filter(hedgeQuote => hedgeQuote)
      }
    };

    this.rfqService
      .tradeService({ task: 'getQuoteDescription', payload })
      .subscribe({
        next: async (r: { description: string; text: string }) => {
          const text = r.text;
          copy(text, { format: 'text/plain' });
          this.copyQuoteState = 'COPIED';
          setTimeout(() => {
            this.copyQuoteState = this.bookTradesCmpt ? 'COPY_TRADE' : null;
          }, 3000);
        },
        error: () => {
          this.copyQuoteState = 'ERROR';
          this.copyQuoteState = this.bookTradesCmpt ? 'COPY_TRADE' : null;
        }
      });
  }

  public get rfqSetupFormValid() {
    return this.rfqTabService.setupValidity;
  }

  public get rfqSubscribed() {
    return this.rfqService.subscribedToRfqs;
  }

  public get mainTimer() {
    return this.rfqTabService.mainTimer;
  }

  public get currentTimestamp() {
    return this.rfqService.currentTimestamp;
  }

  public rfqCommentChanged($event: string) {
    this.rfqTabService.rfqCommentSignal.set($event);
  }

  public get animateQuotes() {
    return this.rfqTabService.animateQuotes;
  }

  public get volBased() {
    return this.rfqTabService.tabData.isVolBased;
  }

  @Throttle(250)
  public get quotes() {
    if (this.legacyWhiteLabelProvider) {
      return {
        bids: this.rfqTabService.quotesBeingDisplayed.bids.filter(
          q => q.bank === this.legacyWhiteLabelProvider
        ),
        offers: this.rfqTabService.quotesBeingDisplayed.offers.filter(
          q => q.bank === this.legacyWhiteLabelProvider
        )
      };
    } else {
      return this.rfqTabService.mungedDisplayedQuotes;
    }
  }

  public get account(): string {
    return this.rfqSetupService.setup.form.value.account || '';
  }

  public get rfeRequestInfo() {
    return this.rfqTabService.rfeRequestInfo;
  }

  public get frozenQuotes(): RfqBidOfferQuotes {
    return this.rfqTabService.frozenQuotes;
  }

  public get rfqDetailsDTO() {
    return this.rfqTabService.rfqDetailsDTO;
  }

  public get rfqBlocked() {
    return (
      this.rfqTabService.tabData.title.status.statusType === RFQ_STATUS_BLOCKED
    );
  }

  public get rfqWaiting() {
    return (
      this.rfqTabService.tabData.title.status.statusType === RFQ_STATUS_WAITING
    );
  }

  public get canCancelRFQ(): boolean {
    return (
      this.rfqRunning &&
      !this.rfqBlocked &&
      !this.rfqWaiting &&
      !this.rfqRequestActionStatus
    );
  }

  @Throttle(500)
  public canSendRFQFromTmpl(): boolean {
    return this.canSendRFQ();
  }

  public canSendRFQ(): boolean {
    const canSend =
      !this.rfqBlocked &&
      !this.rfqRunning &&
      !this.rfqWaiting &&
      !this.rfqPricingMessages.isError &&
      !this.rfqTabService.setupMultiHedgesValidating &&
      this.rfqTabService.tabData.title.productName &&
      this.isMedusaAccountLoggedIn &&
      this.rfqSubscribed &&
      this.rfqSetupFormValid &&
      this.legsValid;
    if (!canSend) {
      this.logCanSendRFQ();
    }
    return canSend;
  }

  public async sendRFQ() {
    this.rfqTabService.clearOldQuotes();
    // Need to wait a bit to avoid showing quotes from previous RFQ
    // Should be slightly greater than the "throttle" time for quotes() (Above)
    await sleep(300);
    this.rfqTabService.legFormsInert.set(false);
    this.copyQuoteState = null;
    this.maxQuotesOnSides = 0;
    this.currBanksState = [];
    console.warn(
      this.rfqTab.title.productName.shortName,
      'is the underlying product type used for this rfq.'
    );
    if (!this.canSendRFQ()) {
      return;
    }
    this.rfqTabService.lastHandleVolBasedHedgeExecution.set(null);
    this.rfqTabService.midPriceInfo.update(_ => null);
    if (
      this.rfqService.rfqTabs.filter(
        tab =>
          tab.title.status.statusType === RFQ_STATUS_PENDING ||
          tab.title.status.statusType === RFQ_STATUS_STREAMING ||
          tab.title.status.statusType === RFQ_STATUS_WAITING
      ).length < 5 // if fewer than 5 banks in an active state
    ) {
      this.rfqTabService.changeTabTile(RFQ_STATUS_WAITING, null);
      this.rfqTabService.initRfqSubmitRequest().subscribe({
        next: response => {
          this.rfqMessagesService.message.set({});
          this.rfqMessagesService.requestId = response.result.id;
          this.removeIndicativePrices.emit();
          this.rfqTabService.startTimer();
          this.rfqSetupService.mifidChecksStatus.update(
            _ => 'ChecksInProgress'
          );
        },
        error: () => {
          this.rfqTabService.changeTabTile(RFQ_STATUS_ERROR, null);
          this.rfqTabService.wipeAll();
        }
      });
    } else {
      this.warningScreenData.warningScreenOpen = true;
      this.warningScreenData.messageToDisplay =
        'Only 5 rfqs are permitted to be active at one time';
      this.warningScreenData.headerMessage = 'Issue with requesting the rfq';
    }
  }

  public changeTopOfBook() {
    this.rfqTabService.tabData.topOfBook =
      !this.rfqTabService.tabData.topOfBook;

    this.pricerToMainTabCommunicator.emit({
      command: RFQ_TOP_OF_BOOK,
      data: this.rfqTabService.tabData.topOfBook
    });
    this.saveChangesToLocalStorage(
      'top-of-book',
      this.rfqTabService.tabData.topOfBook
    );
  }

  public get showTopBookOnly(): boolean {
    return this.rfqTabService.tabData.topOfBook;
  }

  public changeViewMode() {
    this.rfqTab.oneWayView.oneWay = !this.rfqTab.oneWayView.oneWay;
    this.pricerToMainTabCommunicator.emit({
      command: RFQ_VIEW_CHANGE,
      data: this.rfqTab.oneWayView.oneWay
    });
    this.saveChangesToLocalStorage('one-way', this.rfqTab.oneWayView.oneWay);
  }

  public get sideToShow(): string {
    return this.rfqTab.oneWayView.sideToShow;
  }

  public get oneWay() {
    return this.rfqTab.oneWayView?.oneWay;
  }

  public saveNotTradingReason(reason: string): void {
    this.rfqTabService.recordRfqOutcome(
      reason as RfqOutcome,
      this.rfqOutcomeModal().requestId
    );

    this.rfqOutcomeModal.update(state => {
      state.showModal = false;
      state.requestId = null;
      return state;
    });
  }

  private buildCostAnalysisGrid(costAnalysis: CostAnalysis) {
    let grid: string[][] = [];

    const p = costAnalysis.forwardCurve.forwardPoints;
    const pair = costAnalysis.forwardCurve.currencyPair;
    const dp = this.productsConfigService.decimalPlacesForCross(pair);
    grid.push([
      'Spot',
      this.spotOrSpread(costAnalysis.forwardCurve.spot, dp),
      formatNumber(costAnalysis.clientDeltas.spotDelta, 'en-GB', '1.0-0'),
      '0000-00-00' // dummy date to ensure spot is always first row after sorting
    ]);

    Object.entries(p).forEach(([k, v]) => {
      const fd = costAnalysis.clientDeltas.forwardDeltas[k];

      grid.push([
        reformatDate(k, 'D MMM YYYY'),
        this.spotOrSpread(v, 2),
        fd == null ? '' : formatNumber(fd, 'en-GB', '1.0-0'),
        k // so can sort by date
      ]);
    });

    grid.sort((a, b) => a[3].localeCompare(b[3]));

    return grid;
  }

  private spotOrSpread(vals: Points, dp: number): string {
    if (vals.spread === 0) {
      return vals.mid.toFixed(dp);
    } else {
      return `${vals.bid.toFixed(dp)}/${vals.ask.toFixed(dp)}`;
    }
  }

  private saveChangesToLocalStorage(setting: string, value: any) {
    const rfqSavedSettings =
      this.userStorageService.getItem<RfqSettingsState>(RfqSettingsStateKey) ||
      {};

    if (!rfqSavedSettings['pricing-setting']) {
      rfqSavedSettings['pricing-setting'] = {};
    }

    rfqSavedSettings['pricing-setting'][setting] = value;

    this.userStorageService.setItem(RfqSettingsStateKey, rfqSavedSettings);
  }

  public get rfqRequestActionStatus(): RfeAction {
    return this.rfqTabService.rfeRequestInfo;
  }

  public get isMedusaAccountLoggedIn() {
    return this.adminService.isMedusaAccountLoggedIn;
  }

  public rfeRequest(bank: RfqQuote) {
    if (this.rfqService.executionContext.disableExecution === true) {
      this.alertsService.addAlert({
        message: 'RFEs are disabled for this user',
        type: 'error'
      });
      // return;
    }
    if (bank.status === RFQ_QUOTE_INDICATIVE) {
      // indicative prices can't be priced
      return;
    }
    this.rfqTabService.changeTabTile(RFQ_STATUS_PENDING, null);
    this.rfqTabService.wipeAllTimers(); // when a request is sent, all the row timers should be wiped
    this.rfqTabService.executedSide = bank.type;
    this.startRfeTimer(bank.id);
    this.rfqTabService.legFormsInert.set(true);
    this.rfqTabService.sendRfeAction(bank).subscribe({
      error: err => {
        this.rfqTabService.maybeAnimateQuotes(false);
        this.rfqTabService.frozenQuotes = null;
        setTimeout(() => this.rfqTabService.maybeAnimateQuotes(), 0);

        this.rfqTabService.rfqRunning.update(_ => true);
        this.rfqTabService.clearRfeRequestInfo();
        this.warningScreenData.messageToDisplay =
          'An error occurred with the Rfe request';
        this.warningScreenData.headerMessage = 'Issue with the requested rfe';
        this.warningScreenData.warningScreenOpen = true;
        if (err?.error?.exception?.type === 'QuoteExpiredException') {
          this.warningScreenData.messageToDisplay =
            'Quote has expired, please try again';
        }
        this.rfqTabService.startTimer();
        this.rfqTabService.clearRfeTimer();
      }
    });
  }

  public startRfeTimer(id: number) {
    const lastQuoteEvents = this.rfqTabService.lastQuoteEvents();
    const quoteEvent = [...lastQuoteEvents.values()].find(
      q => q.quote.id === id
    );

    const title = this.rfqTabService.tabData.title;
    this.currentRfeExternalID =
      quoteEvent?.quote.externalID ||
      // legacy
      quoteEvent?.quote.externalId ||
      `${quoteEvent?.quote.requestId}`;
    this.rfqTabService.rfeTimer = setTimeout(() => {
      this.cancelRfq(false);
      title.status.statusType = RFQ_STATUS_RFE_PENDING;
      this.rfeTimeoutDialog().nativeElement.showModal();
    }, 60_000);
  }

  public async copyToClipboard(value: any) {
    if (!value) {
      return;
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${value}`);
    } else {
      console.warn('Clipboard API not available (insecure context)');
    }
  }

  public cancelRfq(sendCancelMsg = true): void {
    this.rfqTabService.openNotTradingReasonModal();
    this.rfqTabService.wipeAll(sendCancelMsg);
    this.rfqSetupService.mifidChecksStatus.update(_ => null);
  }

  public warningScreenResponse() {
    this.warningScreenData.warningScreenOpen = false;
  }

  public warningOpenChanged(val: boolean) {
    this.warningScreenData.warningScreenOpen = val;
  }

  public doShowDescription(event: QuoteDescriptionPayload) {
    const description = event.row.description?.[event.side];
    this.selectedQuoteLegDescription = {
      valueChanged: false,
      description
    };
    this.modal.open();
  }

  public get showBank() {
    return this.rfqTabService.providers?.length > 1;
  }

  public pingOnce(): void {
    if (this.hasPingedOnce) {
      return;
    }
    this.hasPingedOnce = true;
    const prefs = this.userStorageService.getItem<UserPreferencesState>(
      UserPreferencesStateKey
    );
    if (!prefs.OtherSettings?.sounds) {
      return;
    }
    this.userStorageService.ping();
  }

  private updateShowBookTrades(): boolean {
    const anyMarginStyleDisabled = !!this.rfqTabService.selectedLegs?.find(
      leg => leg.form.get('marginStyle')?.disabled
    );

    const retVal =
      (!!this.rfqTabService.lastHandleVolBasedHedgeExecution() &&
        this.volBased) ||
      (this.rfqTabService.tabData.useWhiteLabelMargin &&
        this.rfqTab.title.status.statusType === RFQ_STATUS_TRADED);

    const message = {
      retVal,
      lastShowBookTrades: this.lastShowBookTrades,
      anyMarginStyleDisabled,
      lastHandleVolBasedHedgeExecution:
        !!this.rfqTabService.lastHandleVolBasedHedgeExecution(),
      volBased: this.volBased,
      statusType: this.rfqTab.title.status.statusType,
      useWhiteLabelMargin: this.rfqTabService.tabData.useWhiteLabelMargin
    };
    if (!isEqual(this.lastShowBookTradesMessage, message)) {
      console.log('showBookTrades', JSON.stringify(message));
      this.phxService.logEvent({
        type: 'rfq',
        category: 'showBookTrades',
        ...message
      });
    }
    this.lastShowBookTradesMessage = message;

    // Once the Book Trades panel shows, don't hide it (unless starting a new RFQ)
    // This could be at the very top of this function, but need to keep the above
    // debugging for now (until we find why this kludge is even necessary).
    if (this.lastShowBookTrades) {
      return this.lastShowBookTrades;
    }

    if (!retVal || anyMarginStyleDisabled) {
      return false;
    }

    const useBlockTrades =
      this.rfqTabService.tabData.executionContext.useBlockTrades;
    const subAccountEnabled =
      !!this.rfqSetupService.setupForm.get('subAccount')?.enabled;

    if (useBlockTrades && subAccountEnabled) {
      const subAccount =
        this.rfqSetupService.setupForm.get('subAccount')?.value;
      if (this.volBased && !subAccount) {
        if (retVal && !this.subAccountNotSet) {
          this.subAccountNotSet = true;
          this.alertsService.addAlert({
            message: 'Trade can only be booked if a sub account is selected',
            type: 'warning'
          });
        }
        return false;
      }
    }
    if (retVal) {
      this.lastShowBookTrades = true;
    }
    return retVal;
  }

  public quoteChanged(_index: number, item: RfqQuote) {
    return JSON.stringify(item);
  }

  @Throttle(500)
  public get showInlineVol() {
    return !this.volBased && this.rfqTabService.selectedLegs?.length <= 2;
  }

  public get showVolSpreadLine(): boolean {
    return this.volBased;
  }

  private get legsValid() {
    return this.rfqTabService.legsValid;
  }

  private get rfqTab(): RfqTab {
    return this.rfqTabService.tabData as RfqTab;
  }

  /**
   * Utility function to debug reason canSendRFQ is false
   */
  @Throttle(2000)
  private logCanSendRFQ() {
    // run in timeout so any exception doesn't effect main code
    setTimeout(() => {
      const productName =
        this.rfqTabService.tabData.title.productName?.shortName;

      let reasons: Record<string, boolean> = {
        notBlocked: !this.rfqBlocked,
        notRunning: !this.rfqRunning,
        notWaiting: !this.rfqWaiting,
        noError: !this.rfqPricingMessages.isError,
        notValidating: !this.rfqTabService.setupMultiHedgesValidating,
        hasProductName: !!productName,
        loggedIn: this.isMedusaAccountLoggedIn,
        subscribed: this.rfqSubscribed,
        setupFormValid: this.rfqSetupFormValid,
        legsValid: this.legsValid
      };
      // Only log falsy values
      Object.entries(reasons).forEach(([key, value]) => {
        if (value === true) {
          delete reasons[key];
        }
      });

      const legs = this.rfqTabService.selectedLegs?.map(leg =>
        leg.form.getRawValue()
      );

      const event = {
        message: 'RFQ Button disabled',
        reasons: reasons,
        productName: productName ?? 'undefined',
        legs: legs,
        setupData: this.rfqTabService.setupData
      };

      if (isEqual(event, this.lastPhxLogEvent)) {
        return;
      }

      if (Object.values(reasons).length > 0) {
        this.phxService.logEvent({ type: 'rfq', category: 'button', ...event });
      }
      this.lastPhxLogEvent = event;
    });
  }

  private updateTableContainerHeight(
    maxQuotesOnSides: number,
    forceChange = false
  ): void {
    if (
      (this.maxQuotesOnSides > maxQuotesOnSides && !forceChange) ||
      !this.rfqRunning
    ) {
      return;
    }
    this.maxQuotesOnSides = maxQuotesOnSides;
    this.exampleOfQuoteHeight =
      this.bidTableRowContainer()[0]?.nativeElement.offsetHeight ||
      this.offerTableRowContainer()[0]?.nativeElement.offsetHeight ||
      this.exampleOfQuoteHeight ||
      70;

    this.renderer.setStyle(
      this.tableContainer().nativeElement,
      'height',
      `fit-content`
    );
    const numOfAvailableBanks = this.availableProviders(this.currBanksState);

    const currHeight = (this.tableContainer().nativeElement as HTMLElement)
      .offsetHeight;

    const newHeight = Math.max(
      currHeight /* current height */ +
        (numOfAvailableBanks - maxQuotesOnSides) * this.exampleOfQuoteHeight, // add space for other available providers
      currHeight
    );

    this.renderer.setStyle(
      this.tableContainer().nativeElement,
      'height',
      `${newHeight}px`
    );
  }

  private onProviderAvailabilityChange(bankStates: BankState[]): void {
    const changeInAvailability =
      this.availableProviders(this.currBanksState) !==
      this.availableProviders(bankStates);
    this.currBanksState = clone(bankStates);

    if (!this.rfqRunning || !changeInAvailability) {
      return;
    }

    this.updateTableContainerHeight(
      Math.max(
        this.bidTableRowContainer().length,
        this.offerTableRowContainer().length
      ),
      true
    );
  }

  private availableProviders(bankStates: BankState[]): number {
    return bankStates.filter(x => {
      return (
        x.status !== RFQ_PROVIDER_STATE_DECLINED &&
        x.status !== RFQ_PROVIDER_STATE_EXPIRED &&
        x.status !== RFQ_PROVIDER_STATE_UNQUOTED
      );
    }).length;
  }
}
